import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import cls from "./MemberEmail.module.scss";
import api from "@/utils/axios";

const defaultSubject = "A quick update from Noty Universe";

const MemberEmail = () => {
  const navigate = useNavigate();
  const { userId } = useParams();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [form, setForm] = useState({
    subject: "",
    message: "",
    ctaLabel: "",
    ctaUrl: "",
  });

  const loadUser = async () => {
    if (!userId) return;
    try {
      setLoading(true);
      setError("");
      const res = await api.get(`/v1/admin/members/single/${userId}`);
      const fetchedUser = res.data?.user || null;
      if (!fetchedUser) {
        setError("User not found.");
        return;
      }
      setUser(fetchedUser);
      setForm((prev) => ({
        subject: prev.subject || defaultSubject,
        message:
          prev.message ||
          `Hi ${fetchedUser.username || "there"},\n\nWe wanted to share a quick update from the Noty Universe team.\n\nThanks for being part of the community.`,
        ctaLabel: prev.ctaLabel || "",
        ctaUrl: prev.ctaUrl || "",
      }));
    } catch (err) {
      console.error("Failed to load member email info:", err);
      setError("Failed to load member details.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");

    const subject = form.subject.trim();
    const message = form.message.trim();
    const ctaLabel = form.ctaLabel.trim();
    const ctaUrl = form.ctaUrl.trim();

    if (!subject || !message) {
      setError("Subject and message are required.");
      return;
    }

    const payload = {
      subject,
      message,
    };

    if (ctaLabel && ctaUrl) {
      payload.ctaLabel = ctaLabel;
      payload.ctaUrl = ctaUrl;
    }

    try {
      setSending(true);
      await api.post(`/v1/admin/members/email/${userId}`, payload);
      setSuccess("Email queued successfully.");
    } catch (err) {
      console.error("Failed to send email:", err);
      const msg = err?.response?.data?.message || "Failed to send the email.";
      setError(msg);
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className={cls.page}>
        <div className={cls.headerRow}>
          <button
            type="button"
            className={cls.backBtn}
            onClick={() => navigate("/admin/members")}
          >
            ← Back to Members
          </button>
        </div>
        <div className={cls.loadingCard}>
          <span className={cls.spinner} />
          <span>Loading member details...</span>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className={cls.page}>
        <div className={cls.headerRow}>
          <button
            type="button"
            className={cls.backBtn}
            onClick={() => navigate("/admin/members")}
          >
            ← Back to Members
          </button>
        </div>
        <div className={cls.errorCard}>
          <span>{error || "Member not found."}</span>
        </div>
      </div>
    );
  }

  return (
    <div className={cls.page}>
      <div className={cls.headerRow}>
        <button
          type="button"
          className={cls.backBtn}
          onClick={() => navigate(-1)}
        >
          ← Back
        </button>
        <div className={cls.titleBlock}>
          <h2 className={cls.title}>Email Member</h2>
          <p className={cls.subtitle}>
            Send a tailored email to <strong>@{user.username}</strong>.
          </p>
        </div>
      </div>

      <div className={cls.recipientCard}>
        <div>
          <div className={cls.recipientLabel}>Recipient</div>
          <div className={cls.recipientValue}>@{user.username}</div>
        </div>
        <div>
          <div className={cls.recipientLabel}>Email</div>
          <div className={cls.recipientValue}>{user.email}</div>
        </div>
      </div>

      <form className={cls.formCard} onSubmit={handleSubmit}>
        <label className={cls.field}>
          <span>Subject</span>
          <input
            type="text"
            value={form.subject}
            onChange={handleChange("subject")}
            placeholder="Enter subject"
          />
        </label>

        <label className={cls.field}>
          <span>Message</span>
          <textarea
            rows={7}
            value={form.message}
            onChange={handleChange("message")}
            placeholder="Write your message"
          />
        </label>

        <div className={cls.inlineFields}>
          <label className={cls.field}>
            <span>CTA Label (optional)</span>
            <input
              type="text"
              value={form.ctaLabel}
              onChange={handleChange("ctaLabel")}
              placeholder="e.g. View your profile"
            />
          </label>
          <label className={cls.field}>
            <span>CTA URL (optional)</span>
            <input
              type="url"
              value={form.ctaUrl}
              onChange={handleChange("ctaUrl")}
              placeholder="https://notyu.com/..."
            />
          </label>
        </div>

        {error ? <div className={cls.errorBanner}>{error}</div> : null}
        {success ? <div className={cls.successBanner}>{success}</div> : null}

        <div className={cls.actionsRow}>
          <button type="submit" disabled={sending}>
            {sending ? "Sending..." : "Send Email"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default MemberEmail;
